import {Injectable} from '@angular/core';
import {EntryGroupService} from '@app/product/services/entry-group.service';
import {DcpEntryGroup, IAddToCartEventService} from '@dcp/configurator';
import {Cart, CartAddEntrySuccessEvent} from '@spartacus/cart/base/root';
import {EventService} from '@spartacus/core';
import {Configurator} from '@spartacus/product-configurator/rulebased';
import {AdobeTrackingProductData} from '../../../models/adobe-tracking-data';
import {EddlAddToCartEvent} from '../eddl-add-to-cart-event';
import {AddToCartEvent} from './add-to-cart-event';

@Injectable({
  providedIn: 'root'
})
export class AddToCartEventService implements IAddToCartEventService {

  constructor(
    protected eventService: EventService,
    protected entryGroupService: EntryGroupService,
  ) {
  }

  public triggerAddToCartEvent(cart: Cart, data: Configurator.Configuration | CartAddEntrySuccessEvent): void {
    let event: AddToCartEvent;
    if (data && !Object.keys(data).includes('priceSummary')) {
      event = this.createEventForLegacyConfigurator(cart, data as CartAddEntrySuccessEvent);
    } else {
      event = this.createEventForCpsConfigurator(cart, data as Configurator.Configuration);
    }
    if (!event) {
      return;
    }
    this.dispatchEvent(event);
  }

  public triggerEddlAddToCartEvent(data: Configurator.Configuration | CartAddEntrySuccessEvent): void {
    const event = new EddlAddToCartEvent();
    if (data && !Object.keys(data).includes('priceSummary')) {
      event.entry = (data as CartAddEntrySuccessEvent).entry;
    } else {
      event.productCode = (data as Configurator.Configuration).productCode;
    }
    setTimeout(() => this.eventService.dispatch(event, EddlAddToCartEvent));
  }

  protected createEventForLegacyConfigurator(cart: Cart, data: CartAddEntrySuccessEvent): AddToCartEvent {
    if (!cart || !data?.entry?.product) {
      return undefined;
    }
    const event = new AddToCartEvent();
    event.currencyIso = data.entry.totalPrice.currencyIso;
    event.products = this.getRelevantProducts(this.getRelevantLegacyGroups(cart.entryGroups, data.entry.product.code));
    return event;
  }

  protected createEventForCpsConfigurator(cart: Cart, data: Configurator.Configuration): AddToCartEvent {
    if (!cart || !data) {
      return undefined;
    }
    const event = new AddToCartEvent();
    event.currencyIso = data.priceSummary.selectedOptions.currencyIso;
    event.products = this.getRelevantProducts(this.getRelevantCpsGroups(cart.entryGroups, data.productCode));
    return event;
  }

  private getRelevantProducts(entryGroups: DcpEntryGroup[]): AdobeTrackingProductData[] {
    return entryGroups.reduce((acc, entryGroup) => [
      ...acc,
      ...entryGroup.entries.map((entry) => ({
        ID: entry.product.supplierAlternativeAID,
        name: entry.product.name,
        partnerName: entry.product.vendor.name,
        quantity: entry.quantity,
        price: entry.totalPrice.formattedValue,
      }))
    ], []);
  }

  private getRelevantLegacyGroups(entryGroups: DcpEntryGroup[], productCode: string): DcpEntryGroup[] {
    const relevantRootEntryGroups = this.entryGroupService.getRootGroupsByProductCode(entryGroups, productCode);
    return relevantRootEntryGroups.reduce((acc, rootEntryGroup) => [
      ...acc,
      ...rootEntryGroup.entryGroups,
    ], []);
  }

  private getRelevantCpsGroups(entryGroups: DcpEntryGroup[], productCode: string): DcpEntryGroup[] {
    const relevantRootEntryGroups = this.entryGroupService.getRootGroupsByProductCode(entryGroups, productCode);
    return relevantRootEntryGroups.reduce((acc, rootEntryGroup) => [
      ...acc,
      ...this.entryGroupService.filter(
        rootEntryGroup.entryGroups,
        (entryGroup) => !this.entryGroupService.isProduct(entryGroup)
      )
    ], []);
  }

  private dispatchEvent(event: AddToCartEvent): void {
    this.eventService.dispatch<AddToCartEvent>(event);
  }
}
